// src/components/Bienvenue.js
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { creerTombola, saveParametres, ajouterLot } from "../services/db";
import { creerInvitations } from "../services/brevo";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEPS = [
  { id: 1, icon: "🎟️", label: "Votre tombola" },
  { id: 2, icon: "💰", label: "Prix & paiement" },
  { id: 3, icon: "🎁", label: "Les lots" },
  { id: 4, icon: "🤝", label: "Votre équipe" },
  { id: 5, icon: "🚀", label: "C'est parti !" },
];

export default function Bienvenue({ onTermine, onStep1Done }) {
  const { orgId, currentUser } = useAuth();
  const { organisation, loading: orgLoading } = useOrg();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tombolaId, setTombolaId] = useState(null);

  // Étape 1 — Tombola
  const [nom, setNom] = useState("");
  const [annee, setAnnee] = useState(new Date().getFullYear());

  // Étape 2 — Paramètres
  const [prixTicket, setPrixTicket] = useState("2");
  const [methodes, setMethodes] = useState(["Espèces", "Carte Bancaire"]);
  const [newMethode, setNewMethode] = useState("");

  // Étape 3 — Lots
  const [lots, setLots] = useState([
    { description: "", valeur: "", offertPar: "" },
  ]);

  // Étape 4 — Équipe
  const [emailsTexte, setEmailsTexte] = useState("");

  // Récapitulatif exact de ce qui a été fait
  const [recap, setRecap] = useState({ parametres: false, lots: 0, invitations: 0 });

  // ── Étape 1 : Créer la tombola ────────────────────────────────────────────
  async function handleEtape1(e) {
    e.preventDefault();
    if (!nom.trim()) return;
    setLoading(true);
    setError("");
    try {
      // Lever le bouclier AVANT l'écriture Firestore
      // pour que le re-render déclenché par TombolaContext ne cache pas le boulier
      if (onStep1Done) onStep1Done();

      const id = await creerTombola(orgId, { nom: nom.trim(), annee }, currentUser.uid, currentUser.email);
      setTombolaId(id);
      setStep(2);
    } catch (err) {
      console.error("Erreur création tombola:", err.code, err.message);
      setError(`Erreur : ${err.code || err.message}. Réessayez.`);
      // En cas d'échec, on abaisse le bouclier
      sessionStorage.removeItem("boulier_en_cours");
    }
    setLoading(false);
  }

  // ── Étape 2 : Sauvegarder les paramètres ─────────────────────────────────
  async function handleEtape2(skip = false) {
    if (skip) { setError(""); setStep(3); return; }
    setLoading(true);
    try {
      await saveParametres(orgId, tombolaId, {
        prixTicket: parseFloat(prixTicket) || 1,
        methodesEncaissement: methodes,
      });
      setRecap((r) => ({ ...r, parametres: true }));
      setError("");
      setStep(3);
    } catch { setError("Erreur. Réessayez."); }
    setLoading(false);
  }

  // ── Étape 3 : Ajouter les lots ────────────────────────────────────────────
  async function handleEtape3(skip = false) {
    if (skip) { setError(""); setStep(4); return; }
    setLoading(true);
    try {
      const lotsValides = lots.filter((l) => l.description.trim());
      await Promise.all(
        lotsValides.map((l, i) =>
          ajouterLot(orgId, tombolaId, {
            description: l.description.trim(),
            valeur: l.valeur ? (l.valeur.includes("€") ? l.valeur : l.valeur + "€") : "",
            offertPar: l.offertPar.trim(),
            rang: i + 1,
          })
        )
      );
      setRecap((r) => ({ ...r, lots: lotsValides.length }));
      setError("");
      setStep(4);
    } catch { setError("Erreur. Réessayez."); }
    setLoading(false);
  }

  // ── Étape 4 : Inviter les bénévoles ───────────────────────────────────────
  const emailsSaisis = emailsTexte.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);
  const emailsValides = [...new Set(emailsSaisis.filter((e) => EMAIL_REGEX.test(e)))];
  const emailsInvalides = emailsSaisis.filter((e) => !EMAIL_REGEX.test(e));

  async function handleEtape4(skip = false) {
    if (skip) { setError(""); setStep(5); return; }
    if (emailsInvalides.length > 0) { setError(`Email invalide : ${emailsInvalides.join(", ")}`); return; }
    if (emailsValides.length === 0) { setError("Saisissez au moins un email, ou cliquez sur « Plus tard »."); return; }
    setLoading(true);
    setError("");
    try {
      const { envoyees = 0, echecs = [] } = await creerInvitations({
        orgId, tombolaId, emails: emailsValides, role: "benevole",
      });
      setRecap((r) => ({ ...r, invitations: envoyees }));
      if (echecs.length > 0) {
        setError(`${envoyees} invitation(s) envoyée(s). Échec pour : ${echecs.join(", ")}. Vous pourrez réessayer depuis l'onglet Équipe.`);
      }
      setStep(5);
    } catch (err) {
      setError(err.message || "Erreur lors de l'envoi. Vous pourrez réessayer depuis l'onglet Équipe.");
    }
    setLoading(false);
  }

  function addMethode() {
    if (!newMethode.trim() || methodes.includes(newMethode.trim())) return;
    setMethodes([...methodes, newMethode.trim()]);
    setNewMethode("");
  }

  function updateLot(index, field, value) {
    setLots((prev) => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
  }

  function addLot() {
    setLots((prev) => [...prev, { description: "", valeur: "", offertPar: "" }]);
  }

  function removeLot(index) {
    if (lots.length <= 1) return;
    setLots((prev) => prev.filter((_, i) => i !== index));
  }

  // Attendre que l'organisation soit chargée (APRÈS tous les hooks)
  if (orgLoading || !organisation) {
    return (
      <div style={{textAlign:"center",padding:"3rem",color:"#667eea"}}>
        ⏳ Chargement de votre organisation…
      </div>
    );
  }

  return (
    <div style={s.wrapper}>
      {/* Barre de progression */}
      <div style={s.progressBar}>
        {STEPS.map((st) => (
          <div key={st.id} style={s.progressItem}>
            <div style={{
              ...s.progressDot,
              ...(step >= st.id ? s.progressDotActive : {}),
              ...(step === st.id ? s.progressDotCurrent : {}),
            }}>
              {step > st.id ? "✓" : st.icon}
            </div>
            <div style={{
              ...s.progressLabel,
              ...(step >= st.id ? s.progressLabelActive : {}),
            }}>{st.label}</div>
            {st.id < STEPS.length && (
              <div style={{...s.progressLine, ...(step > st.id ? s.progressLineActive : {})}} />
            )}
          </div>
        ))}
      </div>

      {error && <div style={s.errBox}>⚠️ {error}</div>}

      {/* ── ÉTAPE 1 : Tombola ── */}
      {step === 1 && (
        <div style={s.card}>
          <div style={s.cardIcon}>🎟️</div>
          <h2 style={s.cardTitle}>Nommez votre tombola</h2>
          <p style={s.cardDesc}>Quel est le nom de votre événement ?</p>
          <form onSubmit={handleEtape1} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Nom de la tombola *</label>
              <input type="text" placeholder="Ex : Tombola Fête du Club 2026"
                value={nom} onChange={(e) => setNom(e.target.value)}
                required autoFocus style={s.input} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Année <span style={{fontWeight:"400",color:"#aaa",fontSize:"0.78rem"}}>(optionnel)</span></label>
              <input type="number" value={annee || ""}
                onChange={(e) => setAnnee(e.target.value ? Number(e.target.value) : null)}
                min="2020" max="2099" placeholder="2026"
                style={{ ...s.input, width: "120px" }} />
            </div>
            <button type="submit" disabled={loading || !nom.trim()} style={s.btnPrimary}>
              {loading ? "Création…" : "Continuer →"}
            </button>
          </form>
        </div>
      )}

      {/* ── ÉTAPE 2 : Prix & paiement ── */}
      {step === 2 && (
        <div style={s.card}>
          <div style={s.cardIcon}>💰</div>
          <h2 style={s.cardTitle}>Prix & encaissement</h2>
          <p style={s.cardDesc}>Définissez le prix d'un billet et les méthodes de paiement acceptées.</p>

          <div style={s.field}>
            <label style={s.label}>Prix d'un billet (€)</label>
            <div style={s.priceRow}>
              <button type="button" style={s.priceBtn}
                onClick={() => setPrixTicket((p) => String(Math.max(0.5, parseFloat(p) - 0.5)))}>−</button>
              <div style={s.priceDisplay}>{prixTicket}€</div>
              <button type="button" style={s.priceBtn}
                onClick={() => setPrixTicket((p) => String(parseFloat(p) + 0.5))}>+</button>
            </div>
            <input type="number" min="0.5" step="0.5" value={prixTicket}
              onChange={(e) => setPrixTicket(e.target.value)}
              style={{ ...s.input, marginTop: "8px", width: "120px" }} />
          </div>

          <div style={s.field}>
            <label style={s.label}>Méthodes d'encaissement</label>
            <div style={s.methodesList}>
              {methodes.map((m) => (
                <div key={m} style={s.methodeTag}>
                  <span>{m}</span>
                  <button type="button" onClick={() => setMethodes(methodes.filter((x) => x !== m))}
                    style={s.removeBtn}>×</button>
                </div>
              ))}
            </div>
            <div style={s.addRow}>
              <input type="text" placeholder="Ajouter une méthode…"
                value={newMethode} onChange={(e) => setNewMethode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMethode())}
                style={{ ...s.input, flex: 1 }} />
              <button type="button" onClick={addMethode} style={s.btnAdd}>+ Ajouter</button>
            </div>
          </div>

          <div style={s.btnRow}>
            <button onClick={() => handleEtape2(true)} style={s.btnSkip}>Passer cette étape</button>
            <button onClick={() => handleEtape2(false)} disabled={loading} style={s.btnPrimary}>
              {loading ? "Enregistrement…" : "Continuer →"}
            </button>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 3 : Lots ── */}
      {step === 3 && (
        <div style={s.card}>
          <div style={s.cardIcon}>🎁</div>
          <h2 style={s.cardTitle}>Les lots à gagner</h2>
          <p style={s.cardDesc}>Ajoutez vos lots. Le 1er sera le grand prix, le 2e le 2e prix, etc.</p>

          {lots.map((lot, i) => (
            <div key={i} style={s.lotRow}>
              <div style={s.lotRangBadge}>
                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}e`}
              </div>
              <div style={s.lotFields}>
                <input type="text" placeholder="Description du lot *"
                  value={lot.description}
                  onChange={(e) => updateLot(i, "description", e.target.value)}
                  style={s.input} />
                <div style={s.lotMetaRow}>
                  <input type="text" placeholder="Valeur (ex: 50)"
                    value={lot.valeur}
                    onChange={(e) => updateLot(i, "valeur", e.target.value)}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && !v.includes("€")) updateLot(i, "valeur", v + "€");
                    }}
                    style={{ ...s.input, flex: 1 }} />
                  <input type="text" placeholder="🤝 Offert par…"
                    value={lot.offertPar}
                    onChange={(e) => updateLot(i, "offertPar", e.target.value)}
                    style={{ ...s.input, flex: 2 }} />
                  {lots.length > 1 && (
                    <button type="button" onClick={() => removeLot(i)} style={s.removeLotBtn}>🗑️</button>
                  )}
                </div>
              </div>
            </div>
          ))}

          <button type="button" onClick={addLot} style={s.btnAddLot}>
            + Ajouter un lot
          </button>

          <div style={s.btnRow}>
            <button onClick={() => handleEtape3(true)} style={s.btnSkip}>Passer cette étape</button>
            <button onClick={() => handleEtape3(false)} disabled={loading} style={s.btnPrimary}>
              {loading ? "Enregistrement…" : "Continuer →"}
            </button>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 4 : L'équipe ── */}
      {step === 4 && (
        <div style={s.card}>
          <div style={s.cardIcon}>🤝</div>
          <h2 style={s.cardTitle}>Invitez vos bénévoles</h2>
          <p style={s.cardDesc}>
            Chacun reçoit un email avec un lien : il crée son accès en 1 clic et vend
            depuis son propre téléphone. Rien à installer.
          </p>
          <div style={s.form}>
            <label style={s.label}>Emails des bénévoles <span style={{fontWeight:"400",color:"#aaa",fontSize:"0.78rem"}}>(un par ligne)</span></label>
            <textarea rows={4} value={emailsTexte}
              onChange={(e) => { setEmailsTexte(e.target.value); setError(""); }}
              placeholder={"marie@exemple.fr\npaul@exemple.fr"}
              style={{ ...s.input, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }} />
            {emailsValides.length > 0 && (
              <div style={{fontSize:"0.8rem",color:"#2e7d32",fontWeight:"600"}}>
                ✓ {emailsValides.length} invitation{emailsValides.length > 1 ? "s" : ""} prête{emailsValides.length > 1 ? "s" : ""}
              </div>
            )}
            <div style={{fontSize:"0.78rem",color:"#999",lineHeight:1.5}}>
              Vous pourrez inviter d'autres personnes, ou nommer un co-organisateur, depuis l'onglet <strong>Équipe</strong>.
            </div>
            <div style={s.btnRow}>
              <button onClick={() => handleEtape4(true)} style={s.btnSkip}>Plus tard</button>
              <button onClick={() => handleEtape4(false)} disabled={loading} style={s.btnPrimary}>
                {loading ? "Envoi…" : "Envoyer les invitations →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 5 : C'est parti ! ── */}
      {step === 5 && (
        <div style={{ ...s.card, textAlign: "center" }}>
          <div style={s.successAnim}>🎉</div>
          <h2 style={s.successTitle}>Tout est prêt !</h2>
          <p style={s.successDesc}>
            Votre tombola <strong>"{nom}"</strong> est créée.
            Vous pouvez vendre vos premiers billets dès maintenant.
          </p>
          <div style={s.successSteps}>
            <div style={s.successStep}>✅ Tombola créée</div>
            <div style={recap.parametres ? s.successStep : s.todoStep}>
              {recap.parametres ? "✅ Prix et paiements configurés" : "⏳ Prix et paiements : à régler dans Paramètres"}
            </div>
            <div style={recap.lots > 0 ? s.successStep : s.todoStep}>
              {recap.lots > 0 ? `✅ ${recap.lots} lot${recap.lots > 1 ? "s" : ""} ajouté${recap.lots > 1 ? "s" : ""}` : "⏳ Lots : à ajouter dans l'onglet Lots"}
            </div>
            <div style={recap.invitations > 0 ? s.successStep : s.todoStep}>
              {recap.invitations > 0 ? `✅ ${recap.invitations} bénévole${recap.invitations > 1 ? "s" : ""} invité${recap.invitations > 1 ? "s" : ""}` : "⏳ Bénévoles : à inviter dans l'onglet Équipe"}
            </div>
          </div>
          <button onClick={onTermine} style={s.btnSuccess}>
            🎟️ Commencer à vendre des billets !
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  wrapper:{maxWidth:"560px",margin:"0 auto",padding:"0.5rem"},
  // Barre de progression
  progressBar:{display:"flex",alignItems:"flex-start",justifyContent:"center",marginBottom:"2rem",gap:"0"},
  progressItem:{display:"flex",flexDirection:"column",alignItems:"center",position:"relative",flex:1},
  progressDot:{width:"44px",height:"44px",borderRadius:"50%",background:"#f0f0f0",color:"#aaa",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.1rem",fontWeight:"700",transition:"all 0.3s",border:"2px solid #e0e0e0",zIndex:1},
  progressDotActive:{background:"#eef0ff",color:"#667eea",border:"2px solid #667eea"},
  progressDotCurrent:{background:"linear-gradient(135deg,#667eea,#764ba2)",color:"#fff",border:"2px solid transparent",boxShadow:"0 4px 16px rgba(102,126,234,0.4)"},
  progressLabel:{fontSize:"0.7rem",color:"#aaa",fontWeight:"600",marginTop:"6px",textAlign:"center"},
  progressLabelActive:{color:"#667eea"},
  progressLine:{position:"absolute",top:"22px",left:"50%",width:"100%",height:"2px",background:"#e0e0e0",zIndex:0},
  progressLineActive:{background:"#667eea"},
  // Cards
  card:{background:"#f7f8fc",borderRadius:"16px",padding:"2rem",border:"1px solid #eee"},
  cardIcon:{fontSize:"2.5rem",textAlign:"center",marginBottom:"0.75rem"},
  cardTitle:{fontSize:"1.25rem",fontWeight:"800",color:"#1a1a2e",marginBottom:"0.4rem",textAlign:"center"},
  cardDesc:{fontSize:"0.88rem",color:"#888",marginBottom:"1.5rem",textAlign:"center",lineHeight:1.6},
  // Form
  form:{display:"flex",flexDirection:"column",gap:"1.1rem"},
  field:{display:"flex",flexDirection:"column",gap:"6px",marginBottom:"1rem"},
  label:{fontSize:"0.85rem",fontWeight:"700",color:"#444"},
  input:{padding:"0.7rem 1rem",border:"1.5px solid #e0e0e0",borderRadius:"10px",fontSize:"0.95rem",outline:"none",width:"100%",boxSizing:"border-box",background:"#fff"},
  // Prix
  priceRow:{display:"flex",alignItems:"center",gap:"12px"},
  priceBtn:{width:"44px",height:"44px",borderRadius:"50%",border:"2px solid #667eea",background:"#eef0ff",color:"#667eea",fontSize:"1.25rem",cursor:"pointer",fontWeight:"700"},
  priceDisplay:{fontSize:"2rem",fontWeight:"900",color:"#1a1a2e",minWidth:"80px",textAlign:"center"},
  // Méthodes
  methodesList:{display:"flex",flexWrap:"wrap",gap:"8px",marginBottom:"10px"},
  methodeTag:{display:"flex",alignItems:"center",gap:"6px",background:"#eef0ff",color:"#4c5ee8",borderRadius:"20px",padding:"4px 12px",fontSize:"0.85rem",fontWeight:"600"},
  removeBtn:{background:"none",border:"none",cursor:"pointer",color:"#764ba2",fontSize:"1rem",lineHeight:1},
  addRow:{display:"flex",gap:"10px"},
  btnAdd:{padding:"0.6rem 1rem",background:"#eef0ff",color:"#4c5ee8",border:"1px solid #c7d2ff",borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"0.85rem",whiteSpace:"nowrap"},
  // Lots
  lotRow:{display:"flex",gap:"10px",marginBottom:"12px",alignItems:"flex-start"},
  lotRangBadge:{fontSize:"1.5rem",flexShrink:0,marginTop:"8px"},
  lotFields:{flex:1,display:"flex",flexDirection:"column",gap:"6px"},
  lotMetaRow:{display:"flex",gap:"6px",alignItems:"center",flexWrap:"wrap"},
  removeLotBtn:{background:"none",border:"none",cursor:"pointer",fontSize:"1rem",color:"#e53935",flexShrink:0},
  btnAddLot:{width:"100%",padding:"0.65rem",background:"none",border:"1.5px dashed #c7d2ff",borderRadius:"10px",color:"#667eea",fontWeight:"600",cursor:"pointer",fontSize:"0.9rem",marginBottom:"1.25rem"},
  // Boutons
  btnRow:{display:"flex",gap:"10px",alignItems:"center",justifyContent:"space-between"},
  btnPrimary:{flex:1,padding:"0.85rem",background:"linear-gradient(135deg,#667eea,#764ba2)",color:"#fff",border:"none",borderRadius:"10px",fontSize:"0.95rem",fontWeight:"800",cursor:"pointer",boxShadow:"0 4px 16px rgba(102,126,234,0.3)"},
  btnSkip:{background:"none",border:"none",color:"#aaa",fontSize:"0.82rem",cursor:"pointer",padding:"0.5rem",fontWeight:"500",whiteSpace:"nowrap"},
  errBox:{background:"#ffebee",color:"#b71c1c",borderRadius:"10px",padding:"0.75rem 1rem",fontSize:"0.85rem",marginBottom:"1rem"},
  // Succès
  successAnim:{fontSize:"4rem",marginBottom:"1rem"},
  successTitle:{fontSize:"1.5rem",fontWeight:"900",color:"#1a1a2e",marginBottom:"0.5rem"},
  successDesc:{fontSize:"0.92rem",color:"#888",lineHeight:1.6,marginBottom:"1.5rem"},
  successSteps:{display:"flex",flexDirection:"column",gap:"6px",marginBottom:"1.5rem",textAlign:"left",background:"#e8f5e9",borderRadius:"10px",padding:"1rem"},
  successStep:{fontSize:"0.88rem",color:"#2e7d32",fontWeight:"600"},
  todoStep:{fontSize:"0.85rem",color:"#8d6e00",fontWeight:"600"},
  btnSuccess:{width:"100%",padding:"1rem",background:"linear-gradient(135deg,#2e7d32,#43a047)",color:"#fff",border:"none",borderRadius:"12px",fontSize:"1rem",fontWeight:"800",cursor:"pointer",boxShadow:"0 4px 16px rgba(46,125,50,0.3)"},
};
